import { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatTime, formatPrice } from "@/lib/format";
import { Container, PageHero, Seo } from "@/components/site/Ui";
import { MonthCalendar, SlotPicker } from "@/components/booking/Pickers";

const STATUS = { confirmed: "Confermato", cancelled: "Annullato", completed: "Completato", no_show: "Non presentato" };

export default function ManageBooking() {
  const [token, setToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("t") || "");
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const load = async (tok = token) => {
    setError(null);
    try { setAppt((await api.post("/public/booking/manage", { token: tok })).data); } catch (e) { setError(errorMessage(e)); }
  };
  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const act = async (fn) => {
    setError(null); setMsg(null);
    try { await fn(); } catch (e) { setError(errorMessage(e)); }
  };
  const cancel = () => act(async () => {
    if (!window.confirm("Confermi l'annullamento dell'appuntamento?")) return;
    await api.post("/public/booking/manage/cancel", { token });
    setAppt({ ...appt, status: "cancelled", can_cancel: false, can_reschedule: false });
    setMsg("Appuntamento annullato.");
  });
  const reschedule = () => act(async () => {
    const r = await api.post("/public/booking/manage/reschedule", { token, start: slot.start });
    setToken(r.data.manage_token);
    window.history.replaceState(null, "", `#t=${r.data.manage_token}`);
    setAppt(r.data.appointment); setMode(null); setSlot(null);
    setMsg("Appuntamento spostato. Il link precedente non è più valido: usa quello aggiornato in questa pagina o nell'email.");
  });
  const resend = () => act(async () => {
    const r = await api.post("/public/booking/manage/resend", { token });
    setMsg(r.data.status === "sent" ? "Email inviata nuovamente." : `Stato invio: ${r.data.status}`);
  });
  return (
    <>
      <Seo title="Gestisci prenotazione" noindex />
      <PageHero eyebrow="Prenotazione" title="Gestisci il tuo appuntamento" crumbs={[{ label: "Gestisci prenotazione" }]} />
      <Container className="max-w-3xl py-16">
        {!appt && (
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="card-soft grid gap-4 p-8" data-testid="manage-token-form">
            <label className="text-sm">Incolla il link o il codice personale ricevuto
              <input className="field mt-1" value={token} onChange={(e) => setToken(e.target.value.split("#t=").pop().trim())} data-testid="manage-token-input" /></label>
            <button className="btn-primary w-fit" data-testid="manage-token-submit">Apri</button>
          </form>
        )}
        {error && <p role="alert" className="mt-6 text-sm text-red-700" data-testid="manage-error">{error}</p>}
        {msg && <p role="status" className="mt-6 rounded-xl bg-[var(--powder-light)] p-4 text-sm" data-testid="manage-message">{msg}</p>}
        {appt && (
          <div className="card-soft mt-6 p-8" data-testid="manage-appointment">
            <p className="eyebrow">{STATUS[appt.status]} · {appt.ref}</p>
            <h2 className="mt-3 text-3xl">{appt.treatment_name}</h2>
            <p className="mt-3 capitalize" data-testid="manage-when">{formatDate(appt.start)} · {formatTime(appt.start)}</p>
            <p className="mt-1 text-sm text-[var(--muted-ink)]">{appt.duration_min} min · {formatPrice(appt.price_type, appt.price_cents)}</p>
            {appt.status === "confirmed" && !appt.can_cancel && <p className="mt-4 text-sm">Le modifiche online non sono più disponibili: contatta il centro.</p>}
            <div className="mt-8 flex flex-wrap gap-3">
              {appt.can_reschedule && <button className="btn-primary" onClick={() => setMode(mode ? null : "move")} data-testid="manage-reschedule-toggle">Sposta</button>}
              {appt.can_cancel && <button className="btn-outline" onClick={cancel} data-testid="manage-cancel">Annulla appuntamento</button>}
              {appt.status === "confirmed" && <button className="text-sm text-[var(--petrol)] underline" onClick={resend} data-testid="manage-resend">Reinvia email di conferma</button>}
            </div>
            {mode === "move" && (
              <div className="mt-10 grid gap-8 lg:grid-cols-2">
                <MonthCalendar treatmentId={appt.treatment_id} value={date} onChange={(d) => { setDate(d); setSlot(null); }} />
                <div><SlotPicker treatmentId={appt.treatment_id} date={date} value={slot} onChange={setSlot} />
                  <button className="btn-primary mt-6" disabled={!slot} onClick={reschedule} data-testid="manage-reschedule-confirm">Conferma nuovo orario</button></div>
              </div>
            )}
          </div>
        )}
      </Container>
    </>
  );
}
